import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Trash2, Pencil, Plus, X, Check, UserCheck, UserX, Lock, Calendar as CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ABSENCE_REASONS = ["Sick Leave", "Annual Leave", "Training", "Personal", "Other"];

const isCurrentlyOnLeave = (eng) => {
  if (!eng?.absence_start_date || !eng?.absence_end_date) return false;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(eng.absence_start_date);
    const end = new Date(eng.absence_end_date);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  } catch {
    return false;
  }
};

const EngineerDirectoryDialog = ({ open, onOpenChange, onDirectoryChange }) => {
  const { user: currentUser } = useAuth();
  const isHR = currentUser?.role === "hr";

  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingName, setAddingName] = useState("");
  const [addingEmail, setAddingEmail] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [savingId, setSavingId] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/engineer-directory`);
      setEngineers(res.data);
    } catch (e) {
      toast.error("Failed to load engineer directory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addingName.trim() || !addingEmail.trim()) return;
    try {
      await axios.post(`${API}/engineer-directory`, {
        name: addingName.trim(),
        email: addingEmail.trim(),
      });
      toast.success("Engineer added");
      setAddingName("");
      setAddingEmail("");
      fetchList();
      if (onDirectoryChange) onDirectoryChange();
    } catch (e) {
      toast.error("Failed to add engineer");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from directory?`)) return;
    try {
      await axios.delete(`${API}/engineer-directory/${id}`);
      toast.success("Engineer removed");
      fetchList();
      if (onDirectoryChange) onDirectoryChange();
    } catch (e) {
      toast.error("Failed to remove engineer");
    }
  };

  const startEdit = (eng) => {
    setEditingId(eng.id);
    setEditName(eng.name);
    setEditEmail(eng.email);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditEmail("");
  };

  const saveEdit = async (id) => {
    if (!editName.trim() || !editEmail.trim()) return;
    try {
      await axios.put(`${API}/engineer-directory/${id}`, {
        name: editName.trim(),
        email: editEmail.trim(),
      });
      toast.success("Engineer updated");
      cancelEdit();
      fetchList();
      if (onDirectoryChange) onDirectoryChange();
    } catch (e) {
      toast.error("Failed to update engineer");
    }
  };

  const handleToggleAvailability = async (eng) => {
    if (!isHR) return;
    setSavingId(eng.id);
    try {
      const nextAvailable = !(eng.is_available !== false);
      await axios.patch(`${API}/engineer-directory/${eng.id}/availability`, {
        is_available: nextAvailable,
        absence_reason: eng.absence_reason || "",
        absence_start_date: eng.absence_start_date || "",
        absence_end_date: eng.absence_end_date || "",
      });
      toast.success(nextAvailable ? "Marked available" : "Marked unavailable");
      fetchList();
      if (onDirectoryChange) onDirectoryChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update availability");
    } finally {
      setSavingId(null);
    }
  };

  const handleReasonChange = async (eng, reason) => {
    if (!isHR) return;
    setSavingId(eng.id);
    try {
      await axios.patch(`${API}/engineer-directory/${eng.id}/availability`, {
        is_available: eng.is_available !== false,
        absence_reason: reason,
        absence_start_date: eng.absence_start_date || "",
        absence_end_date: eng.absence_end_date || "",
      });
      toast.success("Reason updated");
      fetchList();
      if (onDirectoryChange) onDirectoryChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update reason");
    } finally {
      setSavingId(null);
    }
  };

  const handleDateChange = async (eng, field, value) => {
    if (!isHR) return;
    setSavingId(eng.id);
    try {
      const payload = {
        is_available: eng.is_available !== false,
        absence_reason: eng.absence_reason || "",
        absence_start_date: field === "absence_start_date" ? value : (eng.absence_start_date || ""),
        absence_end_date: field === "absence_end_date" ? value : (eng.absence_end_date || ""),
      };
      await axios.patch(`${API}/engineer-directory/${eng.id}/availability`, payload);
      toast.success("Absence dates updated");
      fetchList();
      if (onDirectoryChange) onDirectoryChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update dates");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl border-[#E4E4E7] rounded-sm max-h-[85vh] overflow-y-auto"
        data-testid="engineer-directory-dialog"
      >
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <DialogTitle
            className="text-2xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
          >
            <Users className="w-6 h-6" />
            Engineer Directory
          </DialogTitle>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-[#71717A]">Manage engineers available for work order assignment</p>
            {isHR ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold bg-[#E0E7FF] text-[#4F46E5] border border-[#C7D2FE]"
                data-testid="hr-badge"
              >
                <UserCheck className="w-3 h-3" />
                HR Mode
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7]"
                data-testid="readonly-badge"
              >
                <Lock className="w-3 h-3" />
                Availability read-only (HR only)
              </span>
            )}
          </div>
        </DialogHeader>

        {/* Add form */}
        <form
          onSubmit={handleAdd}
          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end py-4 border-b border-[#E4E4E7]"
        >
          <div className="space-y-1.5">
            <Label htmlFor="add-name" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
              Name
            </Label>
            <Input
              id="add-name"
              value={addingName}
              onChange={(e) => setAddingName(e.target.value)}
              placeholder="Engineer name"
              required
              className="border-[#E4E4E7] rounded-sm"
              data-testid="add-engineer-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-email" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
              Email
            </Label>
            <Input
              id="add-email"
              type="email"
              value={addingEmail}
              onChange={(e) => setAddingEmail(e.target.value)}
              placeholder="engineer@company.com"
              required
              className="border-[#E4E4E7] rounded-sm"
              data-testid="add-engineer-email"
            />
          </div>
          <Button
            type="submit"
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm h-10"
            data-testid="add-engineer-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </form>

        {/* List */}
        <div className="py-2">
          {loading ? (
            <div className="p-6 text-center text-[#71717A]" data-testid="directory-loading">Loading...</div>
          ) : engineers.length === 0 ? (
            <div className="p-6 text-center text-[#71717A]" data-testid="directory-empty">
              No engineers yet. Add your first engineer above.
            </div>
          ) : (
            <div className="space-y-2" data-testid="engineer-directory-list">
              {engineers.map((eng) => {
                const hasDateRange = !!(eng.absence_start_date && eng.absence_end_date);
                const manuallyUnavailable = eng.is_available === false;
                const onLeaveNow = isCurrentlyOnLeave(eng);
                // Backend precedence: date range wins when both dates are set.
                const effectivelyUnavailable = hasDateRange ? onLeaveNow : manuallyUnavailable;
                return (
                  <div
                    key={eng.id}
                    className={`border rounded-sm p-3 transition-colors ${
                      effectivelyUnavailable ? "border-[#FECACA] bg-[#FEF2F2]" : "border-[#E4E4E7] bg-white"
                    }`}
                    data-testid={`directory-item-${eng.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {editingId === eng.id ? (
                        <>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="border-[#E4E4E7] rounded-sm text-sm"
                              data-testid={`edit-name-${eng.id}`}
                            />
                            <Input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              className="border-[#E4E4E7] rounded-sm text-sm"
                              data-testid={`edit-email-${eng.id}`}
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => saveEdit(eng.id)}
                            className="bg-[#059669] hover:bg-[#047857] text-white rounded-sm"
                            data-testid={`save-edit-${eng.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            className="border-[#E4E4E7] rounded-sm"
                            data-testid={`cancel-edit-${eng.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="w-9 h-9 rounded-sm bg-[#0F172A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {eng.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[#09090B] truncate flex items-center gap-2 flex-wrap">
                              {eng.name}
                              {effectivelyUnavailable ? (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]"
                                  data-testid={`unavailable-badge-${eng.id}`}
                                >
                                  <UserX className="w-3 h-3" />
                                  {onLeaveNow ? "On Leave" : "Unavailable"}
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0]"
                                  data-testid={`available-badge-${eng.id}`}
                                >
                                  <UserCheck className="w-3 h-3" />
                                  Available
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-[#71717A] truncate">{eng.email}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(eng)}
                            className="rounded-sm"
                            data-testid={`edit-btn-${eng.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(eng.id, eng.name)}
                            className="rounded-sm hover:bg-red-50 hover:text-red-600"
                            data-testid={`delete-btn-${eng.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* HR-editable / Admin read-only Availability Section */}
                    {editingId !== eng.id && (
                      <div className="mt-3 pt-3 border-t border-[#E4E4E7] space-y-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleAvailability(eng)}
                            disabled={!isHR || savingId === eng.id}
                            role="switch"
                            aria-checked={eng.is_available !== false}
                            className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                              eng.is_available !== false ? "bg-[#059669]" : "bg-[#71717A]"
                            } ${!isHR ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                            data-testid={`availability-toggle-${eng.id}`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                eng.is_available !== false ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                          <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                            Manual Availability
                          </span>
                        </div>

                        {manuallyUnavailable && (
                          <div className="space-y-1 max-w-xs">
                            <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">
                              Reason (manual absence)
                            </Label>
                            {isHR ? (
                              <Select
                                value={eng.absence_reason || ""}
                                onValueChange={(val) => handleReasonChange(eng, val)}
                                disabled={savingId === eng.id}
                              >
                                <SelectTrigger
                                  className="border-[#E4E4E7] rounded-sm h-9"
                                  data-testid={`absence-reason-select-${eng.id}`}
                                >
                                  <SelectValue placeholder="Select reason" />
                                </SelectTrigger>
                                <SelectContent>
                                  {ABSENCE_REASONS.map((r) => (
                                    <SelectItem
                                      key={r}
                                      value={r}
                                      data-testid={`reason-option-${r.replace(/\s+/g, "-").toLowerCase()}-${eng.id}`}
                                    >
                                      {r}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-sm text-xs font-semibold bg-[#F4F4F5] text-[#09090B] border border-[#E4E4E7] h-9"
                                data-testid={`absence-reason-readonly-${eng.id}`}
                              >
                                <Lock className="w-3 h-3 text-[#71717A]" />
                                {eng.absence_reason || "—"}
                              </span>
                            )}
                          </div>
                        )}

                        <div>
                          <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1.5 flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            Scheduled Absence Window
                            <span className="ml-1 text-[9px] font-normal normal-case tracking-normal text-[#A1A1AA]">
                              (auto-excludes from job assignment when today ∈ range)
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">
                                Absence Start
                              </Label>
                              {isHR ? (
                                <Input
                                  type="date"
                                  value={eng.absence_start_date || ""}
                                  onChange={(e) => handleDateChange(eng, "absence_start_date", e.target.value)}
                                  className="border-[#E4E4E7] rounded-sm h-9 text-xs"
                                  data-testid={`absence-start-${eng.id}`}
                                />
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-1.5 rounded-sm text-xs font-semibold bg-[#F4F4F5] text-[#09090B] border border-[#E4E4E7] h-9"
                                  data-testid={`absence-start-readonly-${eng.id}`}
                                >
                                  <Lock className="w-3 h-3 text-[#71717A]" />
                                  {eng.absence_start_date || "—"}
                                </span>
                              )}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">
                                Absence End
                              </Label>
                              {isHR ? (
                                <Input
                                  type="date"
                                  value={eng.absence_end_date || ""}
                                  onChange={(e) => handleDateChange(eng, "absence_end_date", e.target.value)}
                                  min={eng.absence_start_date || undefined}
                                  className="border-[#E4E4E7] rounded-sm h-9 text-xs"
                                  data-testid={`absence-end-${eng.id}`}
                                />
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-1.5 rounded-sm text-xs font-semibold bg-[#F4F4F5] text-[#09090B] border border-[#E4E4E7] h-9"
                                  data-testid={`absence-end-readonly-${eng.id}`}
                                >
                                  <Lock className="w-3 h-3 text-[#71717A]" />
                                  {eng.absence_end_date || "—"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {onLeaveNow && (
                          <div
                            className="text-[11px] font-semibold text-[#DC2626] bg-[#FEE2E2] border border-[#FECACA] rounded-sm px-2 py-1 flex items-center gap-1"
                            data-testid={`on-leave-indicator-${eng.id}`}
                          >
                            <CalendarIcon className="w-3 h-3" />
                            Currently on leave • auto-excluded from job assignments
                          </div>
                        )}

                        {eng.availability_updated_by && (
                          <div className="text-[10px] text-[#71717A]">
                            Last updated by {eng.availability_updated_by}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-2 text-xs text-[#71717A] border-t border-[#E4E4E7] pt-3">
          {engineers.length} engineer{engineers.length !== 1 ? "s" : ""} in directory
          {!isHR && " • Availability fields are read-only (HR role required to edit)"}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EngineerDirectoryDialog;
